import type { Topic } from "@/lib/topics";

/**
 * German topic pages — written, not machine-translated.
 *
 * Why these exist as their own data rather than going through the runtime
 * translator: a page that is only German after the browser has fetched a
 * translation is not German to a crawler. These render German from the first
 * byte, with no i18n dependency and no async step, which is what makes them
 * indexable.
 *
 * `slug` matches the English page so the two can declare each other as hreflang
 * alternates — and this time the declaration is true.
 *
 * The `reality` block carries the same discipline as the English version: it
 * must track feature-status.ts. If a capability moves there, it moves in both.
 */
export const TOPICS_DE: Topic[] = [
  {
    slug: "agentic-ai-b2b",
    name: "Agentische KI im B2B",
    metaTitle: "Agentische KI im B2B: Was 2026 wirklich funktioniert",
    metaDescription:
      "Agentische KI verspricht autonome Geschäftsagenten. Was heute tatsächlich läuft, was nicht — und wie man es einführt, ohne das Unternehmen darauf zu verwetten.",
    heroLead:
      "Die Kategorie ist laut. Diese Seite ist der Teil, den ein CTO nachprüfen kann — samt Grenzen.",
    thesis: [
      "Agentische KI beschreibt Software, die nicht darauf wartet, bedient zu werden. Sie verfolgt ein Ziel, geht Schritte darauf zu und berichtet zurück. Im B2B ist das tatsächlich neu: Die vorige Werkzeuggeneration hat Informationen für einen Menschen sortiert, der die Arbeit dann selbst gemacht hat.",
      "Das meiste, was darüber geschrieben wird, beschreibt allerdings ein System, das niemand ausgeliefert hat: Agenten, die tausend Verträge unbeaufsichtigt verhandeln, Abschlüsse in einer halben Stunde, Konditionen ohne dass jemand sie liest. Das ist keine Vorsicht gegenüber der Technologie — es ist die Beschreibung von Software, die es noch nicht gibt. Wer danach einkauft, beerdigt sein Pilotprojekt.",
      "Die brauchbare Frage ist enger und beantwortbar: Welchen Teil eines Geschäftsgesprächs kann ein Agent heute führen, mit welchen Belegen, und wo muss weiterhin ein Mensch entscheiden? Genau darauf antwortet diese Seite — und entlang dieser Linie ist das Produkt gebaut.",
    ],
    inPractice: [
      {
        title: "Er antwortet, statt eine E-Mail-Adresse einzusammeln",
        body: "Ein Besucher fragt um 23 Uhr etwas Konkretes. Der Twin antwortet aus dem indexierten Material des Unternehmens und nennt seine Quellen — oder er sagt, dass er es nicht weiss, und bietet einen Menschen an. Das ist ein Agent, der die erste Hälfte eines Verkaufsgesprächs führt, und es läuft heute vollständig.",
      },
      {
        title: "Er sucht einen Grund, bevor er dich unterbricht",
        body: "Das Matching sortiert Unternehmen nicht nach Stichwortähnlichkeit. Es bewertet sieben benannte Signale, danach muss eine Prüfung wörtlich aus dem Text der Gegenseite zitieren. Kann sie das nicht, wird der Treffer nie angezeigt. Die meisten Kandidaten fallen durch — das ist beabsichtigt.",
      },
      {
        title: "Er bereitet vor; du entscheidest",
        body: "Zwei Twins können ein strukturiertes Gespräch mit einem von dir gesetzten Ziel führen. Zurück kommen Transkript und ein Analyseurteil, das belegen muss, was tatsächlich gesagt wurde. Nichts davon bindet dich. Den Vertrag danach unterzeichnet eine namentlich genannte Person, kein Agent.",
      },
    ],
    reality: {
      works: [
        "Belegte Antworten mit Quellen und ein ehrliches „weiss ich nicht“ samt Übergabe an Menschen",
        "Geprüftes Matching mit Belegen — ein nicht begründbarer Treffer wird gar nicht gezeigt",
        "Twin-zu-Twin-Meetings mit Ziel, Transkript und zitierendem Urteil",
        "Verträge und Deals elektronisch unterzeichnet, Konditionen per Hash eingefroren",
      ],
      doesNot: [
        "Agenten vereinbaren keine Preise oder Konditionen in deinem Namen. Verbindliche Aussagen sind aus, bis du sie einschaltest — und auch dann unterschreibt ein Mensch.",
        "Es gibt keinen autonomen Einkauf, kein Ausgabenlimit für Agenten und keine automatische Gegenangebotsschleife.",
        "Nichts ist an SAP oder Salesforce angebunden. Das Wissen kommt von deiner Website und aus hochgeladenen Dokumenten.",
      ],
    },
    queries: [
      "agentische ki b2b",
      "autonome ki agenten unternehmen",
      "ki agenten die verhandeln",
      "was ist agentic ai vertrieb",
      "ki agenten anwendungsfälle b2b",
    ],
    faqs: [
      {
        q: "Kann die KI ohne mich einen Abschluss machen?",
        a: "Nein. Sie kann antworten, qualifizieren, vorbereiten und entwerfen. Jeden Vertrag unterzeichnet eine namentlich genannte Person, und diese Unterschrift hält fest, wer unterschrieben hat, wann, von welcher IP-Adresse und mit welchem SHA-256-Prüfwert des Textes, den sie gesehen hat.",
      },
      {
        q: "Was hindert sie daran, Fähigkeiten zu erfinden, die wir nicht haben?",
        a: "Sie antwortet aus deinem indexierten Material und nennt Quellen. Sie ist angewiesen, „weiss ich nicht“ zu sagen statt zu raten. Jede Antwort landet im Aussagenregister, damit du nachprüfen kannst, was sie Leuten erzählt hat.",
      },
      {
        q: "Worin unterscheidet sich das von einem Chatbot?",
        a: "Ein Chatbot antwortet auf deiner Website. Das hier veröffentlicht zusätzlich ein maschinenlesbares Profil, das Agenten anderer Unternehmen abfragen können, und kann ein strukturiertes Gespräch mit dem Twin eines anderen Unternehmens führen. Der Unterschied liegt in der Erreichbarkeit durch andere Software, nicht in der Gesprächsqualität.",
      },
    ],
  },

  {
    slug: "ai-to-ai-business",
    name: "Geschäft zwischen KI-Systemen",
    metaTitle: "Wenn deine KI mit ihrer KI spricht | Reality Twin",
    metaDescription:
      "Einkäufer recherchieren über KI-Assistenten. Wer nicht maschinenlesbar ist, steht nicht auf der Auswahlliste. Wie Auffindbarkeit zwischen Agenten funktioniert.",
    heroLead: "Dein nächster Kunde lädt deine Website vielleicht nie. Sein Assistent schon.",
    thesis: [
      "Ein wachsender Teil der B2B-Recherche beginnt in einem Assistenten statt in einem Suchfeld. Die Person fragt nach Optionen; das Modell antwortet aus dem, was es erreichen kann. Wenn dein Unternehmen nur für einen Menschen mit Browser lesbar ist, kommst du in dieser Antwort nicht vor.",
      "Das ist eine Veränderung der Distribution, nicht des Marketings. Es ähnelt eher dem Aufkommen von Suchmaschinen als einem neuen Kanal: Erreichbar für die Software zu sein, die heute das Suchen übernimmt, hat man entweder oder nicht.",
      "Handhabbar wird es dadurch, dass die Technik bereits existiert und offen ist. MCP lässt einen Assistenten deinen Twin direkt aufrufen. Eine Agent Card beschreibt dich in einer Form, die Software lesen kann. llms.txt gibt einem Modell den Text ohne das Layout. Nichts davon verlangt, dass jemand einen selbst erfundenen Standard übernimmt.",
    ],
    inPractice: [
      {
        title: "Erreichbar aus ChatGPT und Claude heraus",
        body: "Der MCP-Server bietet Suche, deine Agent Card, einen Frageendpunkt und die Lead-Übergabe. Ein Assistent kann ein Unternehmen finden, sein verifiziertes Profil lesen, dessen Twin etwas fragen und Kontaktdaten übergeben — ohne dass ein Mensch einen Browser öffnet.",
      },
      {
        title: "Ein Profil, geschrieben für Software",
        body: "Die Agent Card unter /.well-known/agent.json nennt Identität, Verifizierungsstatus und wie eine Zusammenarbeit abläuft. Jeder öffentliche Twin stellt eine über HTTP bereit. Das ist der Unterschied zwischen einem Modell, das rät was du tust, und einem, das es liest.",
      },
      {
        title: "Text ohne Oberfläche",
        body: "llms.txt und llms-full.txt veröffentlichen den Inhalt der Seite als reinen Text, damit ein Modell aufnimmt was du sagst statt ein Navigationsmenü. Die Sitemap trägt hreflang für alle Sprachfassungen.",
      },
    ],
    reality: {
      works: [
        "MCP-Server, erreichbar aus Assistenten, die den Standard unterstützen",
        "Agent Card über HTTP für jeden öffentlichen Twin",
        "llms.txt, llms-full.txt, robots.txt und eine Sitemap",
        "Öffentliches Twin-Profil und Marktplatz, den andere Agenten durchsuchen können",
      ],
      doesNot: [
        "Erreichbarkeit garantiert nicht, dass ein Modell dich nennt. Niemand kann eine Platzierung im Modell eines anderen zusagen.",
        "WhatsApp, Slack und Teams sind noch nicht angebunden.",
        "Die Auswertung des Agentenverkehrs existiert, ist aber neu — behandle die Zahlen als Richtungsangabe.",
      ],
    },
    queries: [
      "ki agenten auffindbarkeit b2b",
      "mcp server unternehmen",
      "von chatgpt gefunden werden",
      "agent card a2a",
      "llms.txt für unternehmen",
    ],
    faqs: [
      {
        q: "Muss ich dafür etwas bauen?",
        a: "Nein. Mit der Veröffentlichung deines Twin entstehen die Agent Card, der MCP-Endpunkt und der maschinenlesbare Text. Was du tun musst, ist dem Twin etwas Lesenswertes zu geben.",
      },
      {
        q: "Sehe ich, welche Agenten mich abgefragt haben?",
        a: "Ja, der Agentenverkehr wird aufgezeichnet und in der Auswertung angezeigt. Die Funktion ist jung — lies sie als Signal, nicht als geprüfte Zählung.",
      },
      {
        q: "Ist das nicht einfach SEO mit neuem Namen?",
        a: "Es überschneidet sich, ist aber nicht dasselbe. SEO optimiert eine Seite für einen Ranking-Algorithmus. Das hier macht dein Unternehmen für Software beantwortbar — das Modell bekommt Fakten und Quellen statt einer Seite, die es interpretieren muss.",
      },
    ],
  },

  {
    slug: "trust-layer",
    name: "Die Vertrauensschicht",
    metaTitle: "Verifizierte Firmenidentität für KI-Antworten | Reality Twin",
    metaDescription:
      "Eine KI-Antwort ist so viel wert wie die Identität dahinter. Domainverifizierung, Antworten mit Quellen und ein gehashtes Register jeder Aussage.",
    heroLead:
      "Einen selbstsicheren Chatbot kann jeder auf seine Website stellen. Die Frage ist, wer dahintersteht.",
    thesis: [
      "Das schwierigste Problem kommerzieller KI ist nicht Sprachgewandtheit, sondern Zurechenbarkeit. Ein Modell, das sicher klingt und falsch liegt, kostet mehr als eines, das schweigt — weil jemand danach handelt.",
      "In einem Netzwerk, in dem Agenten mit Agenten sprechen, verschärft sich das: Die Gegenseite sieht keine Website, beurteilt kein Logo und liest keinen Raum. Sie hat, was die andere Partei behauptet. Ohne überprüfbare Identität darunter sind das selbstsichere Fremde, die einander zitieren.",
      "Verifizierung ist hier deshalb kein Abzeichen, sondern das tragende Element. Ein Siegel, das man ohne Nachweis bekommt, ist schlechter als gar keines — es lässt eine Behauptung geprüft aussehen.",
    ],
    inPractice: [
      {
        title: "Domaininhaberschaft, auf drei Wegen nachgewiesen",
        body: "Ein DNS-TXT-Eintrag, ein Meta-Tag oder eine Datei an einem bekannten Pfad. Wer die DNS kontrolliert, kann oft das CMS nicht bearbeiten und umgekehrt — deshalb sind alle drei gleichwertig. Das Siegel erscheint erst, wenn der Token tatsächlich gefunden wurde.",
      },
      {
        title: "Antworten tragen ihre Quellen",
        body: "Der Twin antwortet aus indexiertem Material und nennt, woher jede Aussage stammt. Wo er nichts hat, ist er angewiesen das zu sagen und einen Menschen anzubieten, statt die Lücke zu füllen.",
      },
      {
        title: "Verbindlichkeitsstufen, standardmässig aus",
        body: "Nichts, was der Twin sagt, ist verbindlich, solange du verbindliche Aussagen nicht ausdrücklich aktivierst. Solange sie aus sind, gibt er unverbindliche Orientierung und verweist für eine schriftliche Bestätigung an dein Team. Der Haftungsschutz ist voreingestellt an, nicht aus.",
      },
      {
        title: "Ein Aussagenregister zum Exportieren",
        body: "Jede Antwort nach aussen wird mit Zeitstempel, Quellenangabe und Prüfwert festgehalten. Wenn ein Kunde sagt „Ihre KI hat mir X gesagt“, kannst du nachsehen, ob sie das getan hat.",
      },
    ],
    reality: {
      works: [
        "Domainverifizierung per DNS, Meta-Tag oder Datei",
        "Antworten mit Quellen und ehrlichem „weiss ich nicht“",
        "Verbindlichkeitsstufen — verbindliche Aussagen nur nach ausdrücklicher Freischaltung",
        "Aussagenregister, als JSON exportierbar",
        "Schweizer Unternehmen, Daten in der EU, DSGVO und DSG, AVV und benannte Unterauftragsbearbeiter",
      ],
      doesNot: [
        "Handelsregisternummern werden erfasst, aber noch nicht automatisch gegen das Register geprüft.",
        "Es gibt keine kryptografische Unternehmensidentität und keine Blockchain-ID. Die Verifizierung weist Kontrolle über eine Domain nach — das ist die engere und ehrliche Aussage.",
        "Sanktions- und Bonitätsprüfungen sind nicht Teil des Produkts.",
      ],
    },
    queries: [
      "verifiziertes ki firmenprofil",
      "ki halluzinationen verhindern b2b",
      "ki antwort mit quellen",
      "domainverifizierung unternehmen ki",
      "haftung ki aussagen unternehmen",
    ],
    faqs: [
      {
        q: "Was genau beweist das Verifizierungssiegel?",
        a: "Dass wer den Twin eingerichtet hat, die Domain des Unternehmens kontrolliert. Das ist eine echte, überprüfbare Tatsache. Es wird bewusst nicht als Nachweis der rechtlichen Existenz oder der Zahlungsfähigkeit dargestellt — das sind andere Prüfungen, die wir nicht durchführen.",
      },
      {
        q: "Was passiert, wenn der Twin etwas Falsches sagt?",
        a: "Im Aussagenregister findest du genau, was wann und auf welchen Quellen gesagt wurde. Verbindliche Zusagen sind aus, solange du sie nicht aktiviert hast — eine falsche Antwort ist damit eine Richtigstellung, kein Vertrag.",
      },
      {
        q: "Wo liegen unsere Daten?",
        a: "Die Anwendungsdaten liegen in der Europäischen Union (Irland) und unterliegen der DSGVO sowie dem Schweizer DSG. Reality Twin selbst ist ein Schweizer Unternehmen unter Schweizer Recht. Ein Auftragsbearbeitungsvertrag steht bereit, alle Unterauftragsbearbeiter sind öffentlich aufgeführt.",
      },
    ],
  },

  {
    slug: "end-of-cold-outreach",
    name: "Das Ende der Kaltakquise",
    metaTitle: "Kaltakquise ersetzen durch geprüftes Matching | Reality Twin",
    metaDescription:
      "Kaltakquise wirkt jedes Jahr schlechter. Matching, das belegen muss warum zwei Unternehmen sprechen sollten, ist die bessere Verwendung derselben Stunde.",
    heroLead: "Das Problem der Kaltakquise war nie das Schreiben. Es war das Raten.",
    thesis: [
      "Kaltakquise ist teuer, weil das meiste davon Menschen erreicht, die keinen Grund zu antworten haben. Die Sequenzwerkzeuge des letzten Jahrzehnts haben das Versenden billiger gemacht — und damit die durchschnittliche Nachricht schlechter und das durchschnittliche Postfach gereizter.",
      "Die Alternative ist nicht mehr Personalisierung auf derselben Vermutung. Sie besteht darin, vor dem ersten Satz zu wissen, dass die Gegenseite einen Bedarf genannt hat, den du decken kannst — und zeigen zu können, was sie gesagt hat.",
      "Das verändert die Arbeit. Statt hundert Nachrichten in der Hoffnung auf zwei Antworten bekommst du eine kurze Liste, in der jeder Eintrag ein Zitat aus dem Text der Gegenseite trägt, das erklärt warum er dort steht. Weniger Gespräche, dafür weiter vorn begonnen.",
    ],
    inPractice: [
      {
        title: "Ein Treffer muss sich rechtfertigen",
        body: "Die semantische Suche findet Kandidaten, sieben benannte Signale bewerten sie, dann muss eine Prüfung wörtlich aus dem Text der Gegenseite zitieren. Kein Zitat, kein Treffer. Du siehst die Begründung, nicht nur eine Prozentzahl.",
      },
      {
        title: "Du siehst, wer profitiert",
        body: "Jeder Treffer sagt, ob er beidseitig ist, ob sie dich brauchen oder du sie — entschieden anhand der genannten Bedarfe beider Seiten, nicht anhand eines Mischwerts. Eine einseitige Passung wird als solche ausgewiesen.",
      },
      {
        title: "Der Radar achtet auf Veränderung, nicht auf Bestand",
        body: "Sechs benannte Auslöser: Die Gegenseite hat einen Bedarf notiert, den du deckst; ein ergänzendes Inserat ist erschienen; ein passendes Unternehmen ist beigetreten; eine Gegenseite wurde verifiziert; dein eigener Twin scheitert wiederholt an derselben Frage; oder etwas Vorgemerktes liegt unbearbeitet.",
      },
      {
        title: "Verwerfen ist endgültig",
        body: "Sagst du einmal Nein zu einem Unternehmen, wird es auf Datenbankebene aus künftigen Treffern ausgeschlossen. Ein Werkzeug, das Verworfenes erneut vorschlägt, erzieht dich dazu es zu ignorieren.",
      },
    ],
    reality: {
      works: [
        "Geprüftes Matching mit Zitaten und einsehbarer Signalaufschlüsselung",
        "Deal Radar mit sechs Auslösern und je eigener Halbwertszeit der Dringlichkeit",
        "Anschreiben auf Abruf verfasst, mit Bezug auf den konkreten Auslöser",
        "Dauerhaftes Verwerfen, das eine Gegenseite aus künftigem Matching ausschliesst",
      ],
      doesNot: [
        "Matching kann keine Gegenparteien erschaffen. Das Ergebnis ist durch die Grösse des Netzwerks begrenzt — bei wenigen Teilnehmern ist ein leeres Ergebnis das ehrliche.",
        "Es wird nichts in deinem Namen versendet. Entwürfe entstehen, damit du sie liest, änderst und verschickst.",
        "Es gibt keine E-Mail-Sequenzen, keine Öffnungsverfolgung und keine automatische Nachfasskadenz.",
      ],
    },
    queries: [
      "alternative zur kaltakquise b2b",
      "ki lead matching",
      "b2b partner matching software",
      "bedarfsbasierte akquise",
      "kaltakquise ersetzen",
    ],
    faqs: [
      {
        q: "Verschickt es die E-Mails für mich?",
        a: "Nein. Es verfasst auf Wunsch einen Entwurf, der sich auf den konkreten Grund des Signals bezieht. Verschicken tust du. Ohne dein Zutun verlässt nichts dein Konto.",
      },
      {
        q: "Warum bekomme ich manchmal gar keine Treffer?",
        a: "Weil nichts die Hürde genommen hat. Die Engine erfindet keinen Treffer, um eine Seite zu füllen — sie verwirft Kandidaten, die sie nicht belegen kann, und sagt dir, wie viele Unternehmen zum Zeitpunkt der Suche durchsuchbar waren.",
      },
      {
        q: "Wie kommt der Wert zustande?",
        a: "Aus sieben gewichteten Signalen: genannter Bedarf, was du für sie abdeckst, Profilähnlichkeit, Branchennähe, Markt, Vertrauen in die Gegenseite und jüngste Aktivität. Bei jedem Treffer lässt sich die Aufschlüsselung öffnen.",
      },
    ],
  },

  {
    slug: "ai-negotiation-legal",
    name: "Rechtssicherheit",
    metaTitle: "Wer haftet, wenn eine KI etwas zusagt? | Reality Twin",
    metaDescription:
      "Die Frage, die über die Einführung entscheidet. Wie Verbindlichkeitsstufen, menschliche Unterschrift und gehashte Konditionen einen KI-gestützten Abschluss belastbar machen.",
    heroLead:
      "Der Hemmschuh in den meisten Geschäftsleitungen ist nicht die Technik. Es ist die Frage, wer für das einsteht, was die KI gesagt hat.",
    thesis: [
      "Ein KI-Agent ist keine juristische Person. Nach schweizerischem und europäischem Recht ist er ein Werkzeug, und ein Unternehmen wird durch die Erklärungen seiner Werkzeuge gebunden — ähnlich wie eine Bank durch ihren Geldautomaten. Das ist gefestigt, und genau deshalb zählen die Leitplanken mehr als das Modell.",
      "Die meisten Diskussionen springen sofort zu Smart Contracts und automatischen Preiskorridoren. Irgendwann nützlich, aber sie überspringen die Frage, die ein Jurist zuerst stellt: Kannst du zeigen, was gesagt wurde, gegenüber wem, auf welcher Grundlage und wer es autorisiert hat?",
      "Das ist ein Beweisproblem, bevor es ein Automatisierungsproblem ist — und es ist heute lösbar. Deshalb löst dieses Produkt zuerst dieses Ende.",
    ],
    inPractice: [
      {
        title: "Verbindlich ist aus, bis du es einschaltest",
        body: "Standardmässig verspricht der Twin nie einen Preis, einen Termin oder eine Kondition. Er gibt unverbindliche Orientierung und verweist an dein Team. Nichts kann als verbindlich protokolliert werden, solange die Inhaberin es nicht ausdrücklich aktiviert hat.",
      },
      {
        title: "Ein Mensch unterschreibt, und die Unterschrift trägt Nachweise",
        body: "Verträge werden von einer namentlich genannten Person mit ausdrücklicher Zustimmung unterzeichnet. Der Server erfasst Zeitpunkt, IP-Adresse, Browserkennung und die E-Mail-Adresse — nichts davon wird vom Client übernommen.",
      },
      {
        title: "Der Text wird bei der Unterschrift eingefroren",
        body: "Der Vertragstext wird mit SHA-256 gehasht, und die Unterschrift ist an diesen Prüfwert gebunden. Die gegenzeichnende Partei sendet den Prüfwert zurück, der ihr angezeigt wurde — niemand kann etwas anderes unterschreiben als das Gelesene, und Konditionen lassen sich nicht unter einer Unterschrift hervor ändern.",
      },
      {
        title: "Alles Gesagte ist aktenkundig",
        body: "Das Aussagenregister hält jede Antwort nach aussen fest, mit Zeitstempel und Quellenangabe, und exportiert als JSON für deine Ablage.",
      },
    ],
    reality: {
      works: [
        "Verbindlichkeitsstufen, verbindliche Aussagen standardmässig aus",
        "Elektronische Unterschrift mit Name, Zustimmung, Zeitstempel, IP und Browserkennung",
        "Konditionen per SHA-256 eingefroren; die Gegenzeichnung prüft denselben Wert",
        "Aussagenregister, exportierbar",
        "Schweizer Recht und Gerichtsstand im Rahmenvertrag festgehalten",
      ],
      doesNot: [
        "Es ist eine einfache elektronische Signatur, keine qualifizierte (QES). Für Dienstleistungsverträge nach Schweizer und EU-Recht genügt das in der Regel, für formbedürftige Verträge nicht.",
        "Es gibt keine automatischen Preiskorridore und keine Ausgabenlimits, innerhalb derer ein Agent abschliessen dürfte.",
        "Die Vertragsvorlagen sind für den eigenen Gebrauch der Plattform geschrieben und nicht von externen Anwälten geprüft. Lass sie von deiner Seite lesen, bevor du im grossen Stil darauf baust.",
      ],
    },
    queries: [
      "haftung ki vertrag",
      "wer haftet ki agent vertrag",
      "elektronische signatur schweiz b2b",
      "rechtliche risiken ki verhandlung",
      "verbindliche ki aussagen unternehmen",
    ],
    faqs: [
      {
        q: "Ist eine hier geleistete Unterschrift rechtsgültig?",
        a: "Es handelt sich um eine einfache elektronische Signatur: ausdrückliche Zustimmung plus getippter Name, dazu Zeitstempel, IP und ein Prüfwert des exakten Textes. Für Dienstleistungsverträge nach Schweizer und EU-Recht ist das in der Regel gültig, da keine besondere Form vorgeschrieben ist (Art. 11 OR). Formbedürftige Verträge brauchen eine qualifizierte Signatur — das ist dies nicht.",
      },
      {
        q: "Was, wenn unsere KI etwas sagt, das wir nicht autorisiert haben?",
        a: "Verbindliche Aussagen sind aus, solange du sie nicht aktivierst — standardmässig wird also nichts als Zusage dargestellt. Im Aussagenregister kannst du genau belegen, was auf welchen Quellen gesagt wurde.",
      },
      {
        q: "Können wir Ausgabenlimits für den Agenten setzen?",
        a: "Heute nicht, und wir sagen das lieber, als etwas anderes anzudeuten. Der Agent bereitet vor, ein Mensch unterschreibt. Automatische Limits ergeben erst Sinn, wenn Agenten unbeaufsichtigt abschliessen — und dort ist die Technologie ehrlicherweise nicht.",
      },
    ],
  },

  {
    slug: "sme-market-access",
    name: "Reichweite ohne Vertriebsteam",
    metaTitle: "Globale B2B-Reichweite für kleine Unternehmen | Reality Twin",
    metaDescription:
      "Ein Zehn-Personen-Betrieb kann keine Vertriebsabteilung stellen. Erreichbar, überprüfbar und rund um die Uhr auskunftsfähig sein kann er sehr wohl.",
    heroLead:
      "Der Abstand zwischen kleinem und grossem Lieferanten war nie das Produkt. Es war, wer sich Erreichbarkeit leisten konnte.",
    thesis: [
      "Über weite Strecken der Wirtschaftsgeschichte wurde Marktzugang gekauft. Grössere Unternehmen gewannen nicht, weil ihr Produkt besser war, sondern weil sie die Telefone besetzen, an die Messen fahren und um drei Uhr nachts in einer anderen Zeitzone antworten konnten.",
      "Dieser Vorteil heisst Verfügbarkeit — und Verfügbarkeit ist das eine, was Software wirklich angleicht. Ein Twin, der um zwei Uhr nachts in sieben Sprachen präzise aus deinem eigenen Material antwortet, erledigt den Teil der Arbeit, der früher Personal brauchte.",
      "Er gleicht nicht alles an, und etwas anderes zu behaupten wäre albern. Ruf, Referenzen und Beziehungen begünstigen weiterhin die Etablierten. Aber auffindbar und auskunftsfähig zu sein ist heute von der Unternehmensgrösse ablösbar — und für eine Werkstatt mit neun Leuten und einer Spezialisierung ist das eine echte Verschiebung.",
    ],
    inPractice: [
      {
        title: "Antworten zu jeder Stunde, in sieben Sprachen",
        body: "Der Twin antwortet aus deinem indexierten Material auf Deutsch, Englisch, Spanisch, Französisch, Italienisch, Russisch und Chinesisch — die Anfrage, die sonst bis Montag gewartet hätte, bekommt sofort eine belegte Antwort.",
      },
      {
        title: "Ein öffentliches Profil, das andere Agenten abfragen können",
        body: "Dein Twin ist im Marktplatz gelistet und stellt eine Agent Card bereit. Der Assistent eines Einkäufers kann dich genauso finden und befragen wie ein hundertmal grösseres Unternehmen.",
      },
      {
        title: "Sag, was du suchst",
        body: "Genannte Bedarfe sind das stärkste Signal im Netzwerk. Wer aufschreibt was er sucht, wird in genau der Richtung auffindbar, die ihn interessiert — nicht nur für das, was er ohnehin schon verkauft.",
      },
      {
        title: "Offerieren und über die Plattform bezahlt werden",
        body: "Anfragen kommen herein, du offerierst, der Kunde zahlt online, und was dir zusteht wird in einem Register mit Status festgehalten — nicht bloss in einer E-Mail erwähnt.",
      },
    ],
    reality: {
      works: [
        "Belegte Antworten in sieben Sprachen, rund um die Uhr",
        "Öffentliches Profil, Marktplatzeintrag und Agent Card",
        "Inserate in der Business Exchange mit nächtlichem Abgleich",
        "Bezahlte Dienstleistungsanfragen mit einsehbarem Auszahlungsregister",
        "Ein kostenloser Tarif, der ausreicht um zu prüfen, ob das für dich funktioniert",
      ],
      doesNot: [
        "Die Reichweite ist durch die Netzwerkgrösse begrenzt. Exchange und Matching werden besser, je mehr Unternehmen dabei sind — heute ist diese Zahl klein, und das sollst du vor der Anmeldung wissen, nicht danach.",
        "Auszahlungen erfolgen per Banküberweisung, nicht sofort. Das Register zeigt, was offen ist und wann es rausging.",
        "Es gibt noch keinen Agentur- oder Reseller-Arbeitsbereich zur Verwaltung vieler Twins.",
      ],
    },
    queries: [
      "b2b vertrieb ohne vertriebsteam",
      "ki vertrieb kleine unternehmen",
      "globale reichweite kmu b2b",
      "ki twin für kmu",
      "24/7 anfragen beantworten b2b",
    ],
    faqs: [
      {
        q: "Wir sind neun Leute. Ist das für uns realistisch?",
        a: "Das Antworten, die Mehrsprachigkeit und das öffentliche Profil funktionieren unabhängig von deiner Grösse und ersetzen genau die Verfügbarkeit. Die Netzwerkeffekte — Matching, Exchange, Twin-Meetings — hängen davon ab, wie viele Unternehmen dabei sind, und das ist noch wenig. Beurteile es heute am ersten Teil.",
      },
      {
        q: "Wie lange dauert die Einrichtung?",
        a: "Der Twin baut sich aus deiner Website, danach ergänzt du Dokumente. Die Arbeit steckt darin, was du ihm gibst, nicht in der Konfiguration. Ein dünner Twin antwortet dünn — und das Produkt sagt dir, wo sein Wissen Lücken hat.",
      },
      {
        q: "Was kostet es, das herauszufinden?",
        a: "Es gibt einen kostenlosen Tarif. Er reicht bewusst aus, um zu sehen ob der Twin die echten Fragen deiner Kunden gut beantwortet — denn das ist es, was man vor dem Bezahlen testen sollte.",
      },
    ],
  },
];

export function getTopicDe(slug: string): Topic | undefined {
  return TOPICS_DE.find((t) => t.slug === slug);
}
